package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionState
import dev.lingvo.site.api.rememberSession
import dev.lingvo.site.components.marketing.AuthHandoff
import dev.lingvo.site.ds.ErrorState
import dev.lingvo.site.ds.Space
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import dev.lingvo.site.ds.rememberSettledWait
import dev.lingvo.site.i18n.t

@Composable
fun AuthGuard(
    loading: @Composable () -> Unit = { AppShellLoading() },
    content: @Composable () -> Unit,
) {
    val session = rememberSession()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    // **A wait shorter than the grace period is never painted.**
    //
    // Both states below are transient screens shown while a session resolves, and a cached session
    // resolves in a frame or two. Painting them regardless put a whole app-shell skeleton on screen
    // for ~80ms and tore it away — long enough to see, far too short to read.
    val settled = rememberSettledWait(session is SessionState.Loading || session is SessionState.LoggedOut)

    when (session) {
        SessionState.Loading -> if (settled) loading()
        SessionState.LoggedOut -> {
            LaunchedEffect(Unit) {
                ctx.router.tryRoutingTo("/login", UpdateHistoryMode.REPLACE)
            }
            // **Not `loading()`.** That is the app shell — a sidebar, a rail, the chrome of a
            // signed-in session — and this reader has no session at all. Showing it for the frame
            // before the redirect lands tells somebody they are in, then takes it back. The handoff
            // says the true thing instead, and only if the redirect is slow enough to need saying.
            if (settled) {
                AuthHandoff(t("auth_guard.taking_you_to_sign_in", "Taking you to sign in."))
            }
        }
        is SessionState.Failure -> {
            Div(AppShellRootStyle.toModifier().toAttrs()) {
                Column(
                    Modifier
                        .width(100.percent)
                        // 640, per the spec's card geometry. It was 720.
                        .maxWidth(640.px)
                        .padding(Space.XXXL)
                        .gap(Space.LG),
                ) {
                    // **`session.message` stays off this card, and the reason has moved.**
                    // `ApiTransport` used to fall back to the raw response body whenever the server
                    // sent no structured error, so this card rendered whatever a failing
                    // `/auth/me` returned — a proxy's HTML, a stack trace, a gateway's status text
                    // — under a heading that told the reader it was about them. That is now closed
                    // at the transport: `toApiException` collapses anything unstructured to a
                    // sentence of ours.
                    //
                    // The fixed copy stays regardless, because no message the server can send
                    // answers the two questions somebody actually has here — is my work gone, and
                    // what do I do.
                    ErrorState(
                        message = t(
                            "auth_guard.lingvo_couldnt_confirm_who_you_are",
                            "Lingvo couldn't confirm who you are. Nothing has been saved or " +
                                "lost. Try again, or sign in.",
                        ),
                        title = t("auth_guard.couldnt_verify_your_session", "Couldn't verify your session"),
                        onRetry = { scope.launch { Session.loadMe() } },
                    )
                }
            }
        }
        is SessionState.Authenticated -> {
            content()
            // Mounted here, not in `AppShell`: `EditorLayout` calls `AuthGuard` directly and never
            // calls `AppShell` (D3 keeps the editor's shell-less layout), so a listener installed
            // only inside the shell would be silently dead on the one route somebody has open for
            // eight hours. This is the one composable both `AppLayout` and `EditorLayout` call.
            CommandPaletteHost(ctx.route.path.trimEnd('/').ifBlank { "/" })
        }
    }
}
